import { useState } from 'react';
import { useEventMessagesEnabled } from '@/features/events/hooks/useEventMessagesEnabled';
import { SortDirection } from '@/types/gql.generated';
import type { IconName } from '@/ui/Icon/Icon';
import { FullScreenPanel } from '../FullScreenPanel/FullScreenPanel';
import type { FullScreenSection } from '../FullScreenPanel/FullScreenPanel';
import { useCustomizeParams } from '../useCustomizeParams';
import { MessagesSection } from './MessagesSection';
import type { MessagesView } from './MessagesSection';

export const TextMessagesPanel = () => {
  const { isEnabled: messagesEnabled, isLapsed } = useEventMessagesEnabled();
  const { tab, setTab } = useCustomizeParams();
  // Hoisted so the sort choice survives switching between All/Scheduled/Sent.
  const [sortDirection, setSortDirection] = useState(SortDirection.Desc);

  // Deep links can land here before useNormalizeCustomizeParams strips a gated
  // edit=messages; render nothing until it does. A lapsed workspace still gets
  // in: this panel only lists and reads, so it stays the place to see what was
  // already scheduled.
  if (!messagesEnabled && !isLapsed) return null;

  const buildSection = (
    view: MessagesView,
    icon: IconName,
    label: string
  ): FullScreenSection => ({
    content: () => (
      <MessagesSection
        sortDirection={sortDirection}
        view={view}
        onSortDirectionChange={setSortDirection}
      />
    ),
    icon,
    id: view,
    label,
  });

  const sections: [FullScreenSection, ...FullScreenSection[]] = [
    buildSection('all', 'ListChecks', 'All'),
    buildSection('scheduled', 'Clock', 'Scheduled'),
    buildSection('sent', 'Checkmark', 'Sent'),
  ];

  const activeId =
    tab != null && sections.some((s) => s.id === tab) ? tab : 'scheduled';

  return (
    <FullScreenPanel
      activeId={activeId}
      sections={sections}
      header={{
        highlight: 'events on this planner',
        icon: 'TextMessage',
        subtitlePrefix: 'Text reminders scheduled for ',
        subtitleSuffix: '.',
        title: 'Text Messages',
      }}
      onSelectSection={setTab}
    />
  );
};
